import React, { useContext, useState } from "react";
import {
  Box,
  Flex,
  TransparentButton,
  Text,
  SecondaryButton,
  TertiaryButton,
  Stack,
} from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import OutsideClickHandler from "react-outside-click-handler";
import { css } from "styled-components";

import { Icon, ContentModal } from "~components";
import { BaseContext } from "~context";
import { formatDate } from "~lib";
import { LandingPage } from "~graphql/sdk";

const MotionBox = motion.custom(Box);

interface HeaderProps {
  goBack: () => void;
  title: string;
  startDate: string;
  endDate?: string;
  location: string;
  type?: "event" | "membership";
  accessibility?: string;
  description?: string;
  image?: any;
  supportInfo?: string;
  coordinates?: string;
}

export const Header = ({
  title,
  startDate,
  endDate,
  location,
  coordinates,
  goBack,
  type = "event",
  accessibility,
  description,
  image,
  supportInfo,
}: HeaderProps) => {
  const organization = useContext(BaseContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [isOpenSupport, setIsOpenSupport] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  let backTxt = `Back to ${type === "membership" ? "memberships" : "schedule"}`;

  if (organization?.organization.landingPage === LandingPage.SingleEvent) {
    backTxt = `Back to tickets`;
  }

  return (
    <>
      <Flex
        position="fixed"
        top={0}
        zIndex="popover"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        py="6/4"
        bg="white"
        boxShadow={
          "0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04)" as any
        }
      >
        <Flex flex={1}>
          <Box d={{ _: "none", md: "flex" }} mr={3}>
            <TransparentButton
              css={css({
                transition: "opacity 0.2s",
                "&:hover": {
                  opacity: "0.7",
                },
              })}
              onClick={goBack}
            >
              <Icon icon="backspace" color="N600" fontSize={6} mr="3/4" />
              <Text
                color="N600"
                fontSize={3}
                fontWeight="extraBold"
                lineHeight="medium"
              >
                {backTxt}
              </Text>
            </TransparentButton>
          </Box>
          <Box d={{ _: "flex", md: "none" }} mr="6/4">
            <TransparentButton onClick={goBack}>
              <Icon icon="chevron-left" />
            </TransparentButton>
          </Box>
          <Flex
            borderLeft={{ _: "none", md: "1px" }}
            borderLeftColor={{ _: "N200", md: "N200" }}
            flexDir="column"
            pl={{ _: 0, md: 2 }}
            minW={{ _: "216px", sm: "auto" }}
            w={{ _: "216px", sm: "auto" }}
          >
            <Text
              color="N800"
              fontSize={4}
              fontWeight="heavy"
              lineHeight="normal"
              mb="1/2"
              textTransform="uppercase"
              ellipsis
            >
              {title}
            </Text>
            <Text
              fontSize={2}
              lineHeight="medium"
              fontWeight={{ _: "demiBold", md: "heavy" }}
              d="flex"
              // @ts-expect-error known
              color={{ _: "N700", md: "S300" }}
            >
              {endDate &&
              formatDate(startDate, "dd") !== formatDate(endDate, "dd")
                ? `${formatDate(startDate, "dd LLL")} - ${formatDate(
                    endDate,
                    "dd LLL yyyy"
                  )}`
                : formatDate(startDate, "EEEE d MMMM, p")}
              &nbsp;
              <Box as="span" d={{ _: "none", md: "block" }}>
                - {location}
              </Box>
            </Text>
          </Flex>
        </Flex>
        <Box
          d={{ _: "flex", md: "none" }}
          mr={{ _: 0, md: 2 }}
          position="relative"
        >
          <TransparentButton onClick={() => setShowMenu(true)}>
            <Icon icon="more" color="P300" fontSize={5} />
          </TransparentButton>
          <AnimatePresence>
            {showMenu && (
              <OutsideClickHandler onOutsideClick={() => setShowMenu(false)}>
                <MotionBox
                  position="absolute"
                  top={0}
                  right={0}
                  borderRadius="md"
                  initial={{ opacity: 0, width: 0 }}
                  exit={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  p={2}
                  backgroundColor="white"
                  boxShadow={
                    "0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04)" as any
                  }
                >
                  <Stack flexDir="column" alignItems="flex-start" space={2}>
                    {description && (
                      <TransparentButton
                        fontSize={3}
                        fontWeight="regular"
                        lineHeight="normal"
                        color="N700"
                        whiteSpace="nowrap"
                        onClick={() => setIsOpenInfo(true)}
                      >
                        <Icon icon="info" fontSize={6} color="P300" mr="6/4" />
                        Event info
                      </TransparentButton>
                    )}
                    {accessibility && (
                      <TransparentButton
                        fontSize={3}
                        fontWeight="regular"
                        lineHeight="normal"
                        color="N700"
                        whiteSpace="nowrap"
                        onClick={() => setIsOpen(true)}
                      >
                        <Icon
                          icon="accessibility_purple"
                          fontSize={6}
                          mr="6/4"
                        />
                        Accessibility info
                      </TransparentButton>
                    )}
                    {supportInfo && (
                      <TransparentButton
                        fontSize={3}
                        fontWeight="regular"
                        lineHeight="normal"
                        color="N700"
                        whiteSpace="nowrap"
                        onClick={() => setIsOpenSupport(true)}
                      >
                        <Icon icon="support" fontSize={6} mr="6/4" />
                        Support info
                      </TransparentButton>
                    )}
                  </Stack>
                </MotionBox>
              </OutsideClickHandler>
            )}
          </AnimatePresence>
        </Box>
        <Box d={{ _: "none", md: "flex" }}>
          {accessibility && (
            <TertiaryButton
              px="6/4"
              py={"9px" as any}
              onClick={() => setIsOpen(true)}
            >
              <Icon icon="accessibility" fontSize={5} mr={1} />
              <Text
                color="N600"
                fontSize={2}
                fontWeight="extraBold"
                lineHeight="normal"
              >
                Accessibility
              </Text>
            </TertiaryButton>
          )}
          {description && (
            <SecondaryButton
              ml={2}
              px="6/4"
              py={"9px" as any}
              onClick={() => setIsOpenInfo(true)}
            >
              <Icon icon="info" fontSize={5} color="P300" mr={1} />
              <Text
                color="P300"
                fontSize={2}
                fontWeight="extraBold"
                lineHeight="normal"
              >
                {organization?.organization.landingPage ===
                LandingPage.SingleEvent
                  ? "Ticket"
                  : type === "event"
                  ? "Event"
                  : "Membership"}{" "}
                Info
              </Text>
            </SecondaryButton>
          )}
          {supportInfo && (
            <SecondaryButton
              px="6/4"
              py={"9px" as any}
              ml={2}
              onClick={() => setIsOpenSupport(true)}
            >
              <Icon icon="support" fontSize={5} mr={1} />
              <Text
                color="P300"
                fontSize={2}
                fontWeight="extraBold"
                lineHeight="normal"
              >
                Support
              </Text>
            </SecondaryButton>
          )}
        </Box>
      </Flex>
      <ContentModal
        isOpen={isOpen}
        setIsOpen={() => setIsOpen(false)}
        title="Accessibility"
        richText={accessibility}
      />
      {description && (
        <ContentModal
          isOpen={isOpenInfo}
          setIsOpen={() => setIsOpenInfo(false)}
          title={title}
          image={image}
          richText={description}
          subtitle={location}
          location={location}
          coordinates={coordinates}
          date={
            endDate
              ? `${formatDate(startDate, "dd LLL yyyy")} - ${formatDate(
                  endDate,
                  "dd LLL yyyy"
                )}`
              : formatDate(startDate, "E, dd LLL yyyy p")
          }
        />
      )}
      {supportInfo && (
        <ContentModal
          isOpen={isOpenSupport}
          setIsOpen={() => setIsOpenSupport(false)}
          title="Support information"
          richText={supportInfo}
        />
      )}
    </>
  );
};
